import type {
  AddPaymentEvent,
  AddToCartEvent,
  AddToWishlistEvent,
  BeginCheckoutEvent,
  GA4Event,
  GA4Product,
  PurchaseEvent,
  RemoveFromCartEvent,
  ShippingEvent,
  ViewCartEvent,
  ViewItemEvent,
  ViewItemListEvent,
} from "./event-types";

export * from "./event-types";

declare global {
  interface Window {
    dataLayer?: Object[];
    [key: string]: any;
  }
}

export function sendGTMEvent(data: object) {
  if (typeof window !== "undefined") {
    window.dataLayer ||= [];

    window.dataLayer.push(data);
  }
}

export type GTMProduct = GA4Product;

export type BeforeEvent = (
  data: GA4Event
) => (GA4Event & {}) | Promise<GA4Event & {}>;

export async function addToCart(
  data: AddToCartEvent["ecommerce"],
  beforeEvent?: BeforeEvent
) {
  const event = { event: "add_to_cart", ecommerce: data } as const;
  const eventData = beforeEvent ? await beforeEvent(event) : event;
  sendGTMEvent({ ecommerce: null });
  sendGTMEvent(eventData);
}

export async function addToWishlist(
  data: AddToWishlistEvent["ecommerce"],
  beforeEvent?: BeforeEvent
) {
  const event = { event: "add_to_wishlist", ecommerce: data } as const;
  const eventData = beforeEvent ? await beforeEvent(event) : event;
  sendGTMEvent({ ecommerce: null });
  sendGTMEvent(eventData);
}

export async function beginCheckout(
  data: BeginCheckoutEvent["ecommerce"],
  beforeEvent?: BeforeEvent
) {
  const event = { event: "begin_checkout", ecommerce: data } as const;
  const eventData = beforeEvent ? await beforeEvent(event) : event;
  sendGTMEvent({ ecommerce: null });
  sendGTMEvent(eventData);
}

export async function removeFromCart(
  data: RemoveFromCartEvent["ecommerce"],
  beforeEvent?: BeforeEvent
) {
  const event = { event: "remove_from_cart", ecommerce: data } as const;
  const eventData = beforeEvent ? await beforeEvent(event) : event;
  sendGTMEvent({ ecommerce: null });
  sendGTMEvent(eventData);
}
export async function purchase(
  data: PurchaseEvent["ecommerce"],
  beforeEvent?: BeforeEvent
) {
  const event: PurchaseEvent = { event: "purchase", ecommerce: data };
  const eventData = beforeEvent ? await beforeEvent(event) : event;
  sendGTMEvent({ ecommerce: null });
  sendGTMEvent(eventData);
}

export async function shipping(
  data: ShippingEvent["ecommerce"],
  beforeEvent?: BeforeEvent
) {
  const event = { event: "add_shipping_info", ecommerce: data } as const;
  const eventData = beforeEvent ? await beforeEvent(event) : event;
  sendGTMEvent({ ecommerce: null });
  sendGTMEvent(eventData);
}

export async function viewCart(
  ecommerce: ViewCartEvent["ecommerce"],
  beforeEvent?: BeforeEvent
) {
  const event = { event: "view_cart", ecommerce } as const;
  const eventData = beforeEvent ? await beforeEvent(event) : event;
  sendGTMEvent({ ecommerce: null });
  sendGTMEvent(eventData);
}

export async function viewItem(
  ecommerce: ViewItemEvent["ecommerce"],
  beforeEvent?: BeforeEvent
) {
  const event = { event: "view_item", ecommerce } as const;
  const eventData = beforeEvent ? await beforeEvent(event) : event;
  sendGTMEvent({ ecommerce: null });
  sendGTMEvent(eventData);
}

export async function viewItemList(
  ecommerce: ViewItemListEvent["ecommerce"],
  beforeEvent?: BeforeEvent
) {
  const event = { event: "view_item_list", ecommerce } as const;
  const eventData = beforeEvent ? await beforeEvent(event) : event;
  sendGTMEvent({ ecommerce: null });
  sendGTMEvent(eventData);
}

export async function addPaymentInfo(
  data: AddPaymentEvent["ecommerce"],
  beforeEvent?: BeforeEvent
) {
  const event = { event: "add_payment_info", ecommerce: data } as const;
  const eventData = beforeEvent ? await beforeEvent(event) : event;
  sendGTMEvent({ ecommerce: null });
  sendGTMEvent(eventData);
}

export async function search(search_term: string, beforeEvent?: BeforeEvent) {
  const eventData = { event: "search", search_term } as const;
  const data = beforeEvent ? await beforeEvent(eventData) : eventData;
  sendGTMEvent({ ecommerce: null });
  sendGTMEvent(data);
}

export async function signUp(method: string, beforeEvent?: BeforeEvent) {
  const eventData = { event: "sign_up", method } as const;
  const data = beforeEvent ? await beforeEvent(eventData) : eventData;
  sendGTMEvent(data);
}

export function pageView() {
  sendGTMEvent({ ecommerce: null });
  sendGTMEvent({ event: "page_view", page: window.location.href });
}

export const gtag = {
  addToCart,
  removeFromCart,
  addToWishlist,
  beginCheckout,
  viewCart,
  viewItem,
  viewItemList,
  purchase,
  search,
  signUp,
  pageView,
  shipping,
  addPaymentInfo,
};
